import EngagementMaster from "../../../components/EngagementMaster/EngagementMaster";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  assignEM,
  fetchOrderDetails,
} from "../../../store/slices/resourceManagerSlice";
import "./ViewDetailsPage.scss";
import StagesIndicator from "../../../components/StagesIndicator/StagesIndicator";
import { UserCircleIcon } from "../../../icons/userCircle";
import { useI18n } from "../../../hooks/useI18n";
import { AssignEngagementManagerModal } from "../../../components/CommonUIComponents/AssignEngagementManagerModal/AssignEngagementManagerModal";
import { Button } from "../../../components/CommonUIComponents/Button/Button";
import { ToastMessage } from "../../../components/CommonUIComponents/ToastMessage/ToastMessage";
import { routes } from "../../../routes/routes.constatns";
import {
  isEngagementManager,
  isResourceManager,
} from "../../../util/roleHelpers";
import { parseApiDateAsUtc } from "../../../util/dateUtil";
function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = parseApiDateAsUtc(dateStr);
  if (!d) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function buildFullName(firstName, lastName, fallback) {
  const first = String(firstName || "").trim();
  const last = String(lastName || "").trim();
  const full = [first, last].filter(Boolean).join(" ");
  return full || fallback || "";
}

function mapOrderDetailToEngagementData(detail, fallback = {}) {
  const em = detail?.engagementMaster || {};
  const sd = detail?.serviceDetails || {};

  const engagementManagerName = buildFullName(
    sd.engagementManagerFirstName ?? detail?.engagementManagerFirstName,
    sd.engagementManagerLastName ?? detail?.engagementManagerLastName,
    sd.engagementManager,
  );
  const resourceManagerName = buildFullName(
    sd.resourceManagerFirstName ?? detail?.resourceManagerFirstName,
    sd.resourceManagerLastName ?? detail?.resourceManagerLastName,
    sd.resourceManagerPMO,
  );

  return {
    requestId: em.requestID ?? detail?.requestID ?? fallback?.requestID,
    serviceCatalogueId: em.serviceCatalogueID,
    serviceName: em.serviceName ?? fallback?.serviceName,
    requestStatus:
      em.requestStatus ?? detail?.currentStageName ?? fallback?.status,
    clientCode: em.clientCode ?? fallback?.clientCode,
    clientName: em.clientName ?? fallback?.clientName,
    workspaceName: em.workspaceName,
    office: em.office ?? fallback?.office,
    country: em.country ?? fallback?.country,
    region: em.region ?? fallback?.region,
    sector: em.sector ?? fallback?.sector,
    serviceLine: em.serviceLine,
    subServiceLine: em.subServiceLine,
    engagementId: em.engagementID,
    engagementName: em.engagementName ?? fallback?.engagementName,
    archivedEngagementId: em.archivedEngagementID,
    expectedEngagementHours: em.expectedEngagementHours,
    engagementNature: em.engagementNature,
    entityType: em.entityType,
    multiEntity: em.multiEntity,
    complexity: em.complexity,

    createdBy: sd.createdBy,
    creationDate: formatDate(sd.creationDate),
    yearEndDate: formatDate(sd.yearEndDate),
    startDate: formatDate(sd.startDate),
    endDate: formatDate(sd.endDate),
    natureOfEngagement: sd.engagementNature ?? em.engagementNature,
    targetDeliveryDate: formatDate(sd.endDate),
    accessReceivedDate: formatDate(sd.startDateOfSubstantiveProcedure),
    actualDeliveryDate: formatDate(sd.completionDateOfSubstantiveProcedure),
    expectedCompletionDate: formatDate(sd.expectedCompletionDate),
    allocationDate: formatDate(sd.allocationDate),
    revisedStartDate: formatDate(sd.revisedStartDate),
    revisedEndDate: formatDate(sd.revisedEndDate),
    resourceManagerPMO: resourceManagerName || sd.resourceManagerPMO,
    resourceManagerEmail:
      sd.resourceManagerEmail ?? sd.resourceManagerPMO ?? detail?.resourceManagerEmail ?? null,
    engagementManager: engagementManagerName || sd.engagementManager,
    engagementManagerEmail:
      detail?.engagementManagerEmail ?? sd.engagementManagerEmail ?? sd.engagementManager ?? null,
    engagementDelegates: Array.isArray(sd.engagementDelegates)
      ? sd.engagementDelegates
      : [],
    teamMembers: sd.teamMembers,
    industry: sd.industry ?? null,
    comments: sd.comments || detail?.comments || [],

    history: detail?.history || [],
  };
}

const ViewDetailsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { domain } = useParams();
  const { orderDetail } = useSelector((store) => store.resourceManager);
  const { t } = useI18n();

  const clickedRowData = useMemo(() => state?.engagementData || {}, [state]);
  const requestId = clickedRowData?.requestID ?? clickedRowData?.requestId;

  const roleData = useSelector((state) => state.landing.roleData);
  const userIsResourceManager = isResourceManager(roleData?.role);
  const userIsEngagementManager = isEngagementManager(roleData?.role);

  // viewContext decides which view (RM vs EM) renders on this page.
  // Primary source: the `from` value set on navigation from the list pages.
  // Fallback (direct URL access / missing state): prefer RM when the user has
  // that role, otherwise EM. Matches the old single-role behavior.
  const navFrom = state?.from;
  const viewContext =
    navFrom === routes.resourceManager
      ? "resource"
      : navFrom === routes.engagementManager
        ? "engagement"
        : userIsResourceManager
          ? "resource"
          : userIsEngagementManager
            ? "engagement"
            : null;
  const isResourceView = viewContext === "resource";
  const isEngagementView = viewContext === "engagement";

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const { assignLoading } = useSelector((state) => state.resourceManager);
  const currentUser = useSelector((state) => state.user.user);
  const actingUserEmail = currentUser?.username || currentUser?.email || "";

  useEffect(() => {
    if (!requestId) return;
    dispatch(
      fetchOrderDetails({
        requestId: Number(requestId),
        domain,
        engagementManagerMailId: actingUserEmail || null,
      }),
    );
  }, [dispatch, requestId, domain, actingUserEmail]);

  const engagementData = useMemo(
    () => mapOrderDetailToEngagementData(orderDetail, clickedRowData),
    [orderDetail, clickedRowData],
  );

  const currentStageId = Number(
    orderDetail?.currentStageOrder ?? clickedRowData?.currentStageOrder ?? 1,
  );
  const isTerminal = orderDetail?.isTerminal === true;
  const isAcknowledged = orderDetail?.isAcknowledged === true;
  const isDisabled = isTerminal || !isAcknowledged;
  const isCurrentUserDelegate = orderDetail?.isCurrentUserDelegate === true;
  const orderStatus = orderDetail?.status || "";
  const isOrderAssigned = orderStatus.toLowerCase() !== "unassigned";

  const handleAssign = async ({ email, firstName, lastName }) => {
    const currentRequestId = Number(
      engagementData?.requestId ?? requestId ?? orderDetail?.requestID,
    );
    if (!currentRequestId) return;

    const requestIds = [currentRequestId];

    const result = await dispatch(
      assignEM({
        requestIds,
        engagementManagerEmail: email,
        firstName,
        lastName,
        actingUserEmail,
        domain,
      }),
    );

    if (!result.error) {
      setAssignError("");
      setShowAssignModal(false);
      setToastMessage("Engagement Manager assigned successfully!");
      setToastVariant("success");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      dispatch(
        fetchOrderDetails({
          requestId: Number(requestId),
          domain,
          engagementManagerMailId: actingUserEmail || null,
        }),
      );
    } else {
      const errMsg =
        typeof result.payload === "string"
          ? result.payload
          : result.payload?.message || "Failed to assign Engagement Manager";
      setAssignError(errMsg);
    }
  };

  return (
    <main className="view-order-details">
      {isResourceView && (
        <section className="view-order-rm-tab">
          <p className="motif-h6-default-bold">
            {t("vieworder", { defaultValue: "View Order" })}
          </p>
          {!isOrderAssigned && (
            <Button
              className="rmq-outline-btn"
              type="button"
              onClick={() => setShowAssignModal(true)}
              size="medium"
              variant="secondary"
            >
              <span className="rmq-outline-btn-icon">
                <UserCircleIcon size={16} color="currentColor" />
              </span>

              {t("rmq_assignEngagementManager", {
                defaultValue: "Assign Engagement Manager",
              })}
            </Button>
          )}
        </section>
      )}

      {isEngagementView && (
        <section className="view-order-section">
          <div>
            <StagesIndicator
              currentStageId={currentStageId}
              isTerminal={isTerminal}
              previousStageOrder={orderDetail?.previousStageOrder}
              stages={orderDetail?.stages}
            />
          </div>
        </section>
      )}
      <EngagementMaster
        data={engagementData}
        currentStageId={currentStageId}
        isTerminal={isTerminal}
        isDisabled={isDisabled}
        isCurrentUserDelegate={isCurrentUserDelegate}
        domain={domain}
        viewContext={viewContext}
        onAssignSuccess={() => {
          const redirectPage = isEngagementView
            ? routes.engagementManager
            : routes.resourceManager;
          navigate(`/${domain}/${redirectPage}`, {
            state: { toast: "Engagement Manager changed successfully!" },
          });
        }}
      />

      <AssignEngagementManagerModal
        show={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setAssignError("");
        }}
        onAssign={handleAssign}
        loading={assignLoading}
        error={assignError}
        onClearError={() => setAssignError("")}
        note={t("modal_assignEM_note", {
          defaultValue:
            "Ensure that you assign requests to employees who are either Seniors or higher ups.",
        })}
      />

      {showToast && (
        <ToastMessage
          variant={toastVariant}
          message={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
    </main>
  );
};

export default ViewDetailsPage;
